/**
 * Monitor building grouping (buildings_id + building_name from /getroomstatuses).
 */

export function getMonitorBuildingKey(floorOrRoom) {
    if (!floorOrRoom || typeof floorOrRoom !== 'object') return 'default';
    const id = floorOrRoom.buildings_id;
    if (id != null && String(id).trim() !== '') return String(id);
    const name = floorOrRoom.building_name;
    if (name != null && String(name).trim() !== '') return String(name).trim().toLowerCase();
    return 'default';
}

export function getMonitorBuildingDisplayName(floorOrRoom) {
    const name = floorOrRoom?.building_name;
    if (name != null && String(name).trim() !== '') return String(name).trim();
    return 'Building';
}

export function getMonitorFloorRowKey(floor) {
    return `${getMonitorBuildingKey(floor)}-${floor.floor}`;
}

export function monitorPropertyHasMultipleBuildings(floors) {
    if (!Array.isArray(floors) || floors.length === 0) return false;
    const keys = new Set();
    floors.forEach((floor) => keys.add(getMonitorBuildingKey(floor)));
    return keys.size > 1;
}

/** True when /getroomstatuses attached buildings_id or building_name to at least one floor row. */
export function monitorPropertyHasBuildingData(floors) {
    if (!Array.isArray(floors) || floors.length === 0) return false;
    return floors.some((floor) => getMonitorBuildingKey(floor) !== 'default');
}

export function sortMonitorFloorsDesc(floors) {
    return [...floors].sort((a, b) => {
        const buildingCompare = getMonitorBuildingDisplayName(a).localeCompare(
            getMonitorBuildingDisplayName(b),
            undefined,
            { sensitivity: 'base' }
        );
        if (buildingCompare !== 0) return buildingCompare;
        return b.floor - a.floor;
    });
}

export function groupMonitorFloorsByBuilding(floors) {
    const buildingMap = new Map();
    floors.forEach((floor) => {
        const key = getMonitorBuildingKey(floor);
        if (!buildingMap.has(key)) {
            buildingMap.set(key, {
                buildings_id: floor.buildings_id ?? null,
                building_name: getMonitorBuildingDisplayName(floor),
                floors: []
            });
        }
        buildingMap.get(key).floors.push(floor);
    });

    return Array.from(buildingMap.values())
        .sort((a, b) =>
            String(a.building_name).localeCompare(String(b.building_name), undefined, {
                sensitivity: 'base'
            })
        )
        .map((building) => ({
            ...building,
            floors: [...building.floors].sort((a, b) => b.floor - a.floor)
        }));
}

export function getMonitorBuildingGroupingStorageKey(login) {
    if (!login || typeof login !== 'object') {
        return 'roomOpsMonitor_buildingGrouping_default';
    }
    const pid = String(login.properties_id ?? login.propertiesId ?? '0');
    const user = String(
        login.email ||
            login.users_id ||
            login.user_id ||
            login.username ||
            'default'
    );
    return `roomOpsMonitor_buildingGrouping_${pid}_${user}`;
}

export function readStoredBuildingGrouping(login) {
    try {
        const key = getMonitorBuildingGroupingStorageKey(login);
        const value = localStorage.getItem(key);
        return value !== '0';
    } catch {
        return true;
    }
}

export function writeStoredBuildingGrouping(login, show) {
    try {
        localStorage.setItem(getMonitorBuildingGroupingStorageKey(login), show ? '1' : '0');
    } catch {
        /* ignore */
    }
}
